import { motion } from 'framer-motion';
import PrimaryButton from '../ui/PrimaryButton';

export default function LandingPage() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-start">
        {/* Background Image */}
        <div className="absolute inset-0 bg-cover bg-center z-0" 
             style={{ backgroundImage: 'url(/images/harbourside_kids.jpg)' }}></div>
        {/* <div className="absolute inset-0 bg-black bg-opacity-20"></div> */}
        
        {/* Content */}
        <div className="relative max-w-6xl mx-auto px-6 text-left" style={{ zIndex: 20 }}>
          <motion.div
            className="bg-white bg-opacity-90 p-8 rounded-lg max-w-lg"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.h1 
              className="text-4xl md:text-5xl font-bold text-dblue mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Welcome to<br />
              <span className="text-yellow-500">Harbourside Children's Center</span>
            </motion.h1>
           <motion.div
              className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="flex items-start gap-3">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wide text-amber-700">
                    North Harbour Families Promotion
                  </p>
                  <p className="mt-1 text-sm text-dblue leading-relaxed">
                    Residents from North Harbour can enjoy a 0$ application fee! Book a tour to secure your spot and take advantage of this exclusive offer.
                  </p>
                </div>
              </div>
            </motion.div>
 
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <PrimaryButton text="Book a tour" href="enrollment" />
            </motion.div>
            
          </motion.div>
        </div>
        
      </section>

      {/* Welcome Section */}
      <section className="py-16 bg-gray-50">
            <div className="max-w-4xl mx-auto px-6 text-center">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-dblue mb-6">
                  Welcome to Harbourside Children Center
                </h2>
                <p className="text-gray-600 leading-relaxed max-w-3xl mx-auto">
                  We are proudly located in the beautiful Harbourside Community of North Vancouver—just
                  minutes from the scenic North Shore Sea Wall and Lonsdale Quay. Our Centre
                  offers a warm, supportive environment for children to learn, grow, and explore,
                  surrounded by nature and community.
                </p>
              </motion.div>
            </div>
          </section>

      {/* Three Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Our Education */}
            <motion.div 
              className="text-center p-6"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-dblue" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-dblue mb-2">High-Quality Childcare</h3>
              <p className="text-gray-600 text-sm">
                At Harbourside Children’s Centre (HCC), our goal is to
                provide high-quality childcare where children feel
                secure, autonomous, and competent. We aim to
                empower children to respect and value themselves
                and others by creating a relaxed, warm, supportive,
                and nurturing environment.
              </p>
            </motion.div>

            {/* Our Classrooms */}
            <motion.div 
              className="text-center p-6"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-dblue" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-dblue mb-2">Supporting Growth & Development:</h3>
              <p className="text-gray-600 text-sm">
              We listen carefully to ensure each child feels heard
              and respected. We offer resources and opportunities
              for learning in all areas of development: social,
              emotional, physical, and intellectual. Our activities
              are developmentally appropriate and challenging,
              fostering a loving and supportive atmosphere that
              encourages self-worth              </p>
            </motion.div>



            {/* Our Staff */}
            <motion.div 
              className="text-center p-6"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-pink-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-dblue mb-2">Our Philosophy</h3>
              <p className="text-gray-600 text-sm">
              Emphasizing respect, individuality, and exploration,
              fostering a nurturing environment where children
              develop confidence, express their ideas, and build
              strong connections within a supportive community
              </p>
            </motion.div>
          </div>
        </div>
      </section>

    

    </div>
  );
}
